import { useNavigate } from "react-router-dom";
import { ChevronDown, Shield, Building2, Users, LayoutDashboard, Crown } from "lucide-react";
import { useUserRole, UserRole } from "@/hooks/useUserRole";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const roleConfig: Record<string, { label: string; icon: typeof Shield; href: string; color: string }> = {
  internal_admin: { label: "Admin", icon: Shield, href: "/admin", color: "bg-accent text-accent-foreground" },
  internal_reviewer: { label: "Reviewer", icon: Shield, href: "/admin", color: "bg-primary/20 text-primary" },
  ceo: { label: "CEO", icon: Crown, href: "/ceo", color: "bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-orange-600 border border-orange-300/50" },
  seller: { label: "Seller", icon: Building2, href: "/seller", color: "bg-secondary text-secondary-foreground" },
  buyer: { label: "Buyer", icon: Users, href: "/buyer", color: "bg-muted text-muted-foreground" },
};

export const RoleSwitcher = () => {
  const { roles, primaryRole, loading } = useUserRole();
  const navigate = useNavigate();

  // Don't show if loading or only one role
  if (loading || roles.length <= 1) {
    // Show single role badge if available
    if (!loading && primaryRole && roleConfig[primaryRole]) {
      const config = roleConfig[primaryRole];
      return (
        <span className={`px-2 py-0.5 text-xs rounded-full ${config.color}`}>
          {config.label}
        </span>
      );
    }
    return null;
  }

  const currentConfig = primaryRole ? roleConfig[primaryRole] : null;

  const handleRoleSwitch = (role: UserRole) => {
    if (role && roleConfig[role]) {
      navigate(roleConfig[role].href);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-accent/50 hover:bg-accent transition-colors">
        {currentConfig ? (
          <>
            <currentConfig.icon className="w-3 h-3" />
            {currentConfig.label}
          </>
        ) : (
          <>
            <LayoutDashboard className="w-3 h-3" />
            Switch Role
          </>
        )}
        <ChevronDown className="w-3 h-3 ml-1" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[150px]">
        {roles.map((role) => {
          if (!role || !roleConfig[role]) return null;
          const config = roleConfig[role];
          const Icon = config.icon;
          const isActive = role === primaryRole;
          
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => handleRoleSwitch(role)}
              className={`flex items-center gap-2 cursor-pointer ${isActive ? 'bg-accent/50' : ''}`}
            >
              <Icon className="w-4 h-4" />
              <span>{config.label}</span>
              {isActive && <span className="ml-auto text-xs text-muted-foreground">current</span>}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};